import { UseTellescopeQueryOptions } from "../../types";
import { useTellescopeQuery } from "../../use-tellescope-query";

/** Raw AWS SES verification state. */
export type SESVerificationStatus = "Pending" | "Success" | "Failed" | "TemporaryFailure" | "NotStarted";

/** Simplified status derived from the raw SES verification state. */
export type SESSimplifiedStatus = "pending" | "verified" | "failed";

export interface SESDnsRecord {
  type: "CNAME" | "MX" | "TXT";
  name: string;
  value: string;
}

/** Payload returned when the domain is registered in SES. */
export interface SESDomainStatus {
  domain: string;
  /** Simplified verification status (overrides the raw `verificationStatus`). */
  status: SESSimplifiedStatus;
  /** Raw SES domain verification status. */
  verificationStatus: SESVerificationStatus;
  /** Raw SES DKIM verification status. */
  dkimStatus: SESVerificationStatus;
  dkimTokens: string[];
  /** e.g. `tellescopemail.acme-health.com`. */
  mailFromDomain: string;
  /** Simplified mail-from status (overrides the raw SES mail-from status). */
  mailFromStatus: SESSimplifiedStatus;
  /** DNS records the customer must add to verify the domain. */
  dnsRecords: {
    /** One CNAME per DKIM token. */
    dkim: SESDnsRecord[];
    mailFrom: {
      mx: SESDnsRecord;
      txt: SESDnsRecord;
    };
  };
}

/** Payload returned when the domain is not registered in SES. */
export interface SESDomainNotFound {
  domain: string;
  status: "not_found";
}

export interface SESDomainStatusResponse {
  data: SESDomainStatus | SESDomainNotFound;
}

export interface UseSESDomainStatusQueryOptions<TError = Error, TData = SESDomainStatusResponse>
  extends Omit<UseTellescopeQueryOptions<"integrations", "proxy_read", TError, TData>, "model" | "method" | "request"> {
  /** The email sending domain to check (e.g. `acme-health.com`). Must exist in the org's `verifiedEmailDomains`. */
  domain: string;
}

/**
 * Fetches the live AWS SES verification state and required DNS records for an email sending domain.
 *
 * Admin-only: the backend returns `403` unless the caller is an admin user, and `400` unless the
 * domain already exists in the organization's `verifiedEmailDomains` (managed on the Organization
 * object via `organizations.getOne`/`getSome`, not this proxy).
 *
 * Both `status` and `mailFromStatus` in the response are simplified (`pending` | `verified` |
 * `failed`); the raw `verificationStatus` and `dkimStatus` fields remain available unmodified. If
 * the domain is not registered in SES, `status` is `'not_found'` and the DNS/token fields are absent.
 */
export const useSESDomainStatusQuery = <TError = Error, TData = SESDomainStatusResponse>({
  domain,
  ...options
}: UseSESDomainStatusQueryOptions<TError, TData>) =>
  useTellescopeQuery({
    model: "integrations",
    method: "proxy_read",
    request: {
      integration: "SES",
      type: "domain-status",
      id: domain,
    },
    enabled: Boolean(domain),
    ...options,
  });
